import type { FC } from 'react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCountryAutocomplete } from '@components/moleculs/autocomplete/hooks/use-country-autocomplete';
import { FormAutocomplete } from '@components/organisms/forms/form-autocomplete';
import { FormDatePicker } from '@components/organisms/forms/form-date-picker';
import { FormTextField } from '@components/organisms/forms/form-text-field';
import { FORM_MAX_LENGTH } from '@constants';
import { useCountryOptions, useGenderOptions } from '@hooks/options';
import { ManualFieldNamesEnum } from '@models/enums';
import { usePersonalData } from '@modules/dashboard/pages/docs-verification/docs-verification-page/tabs/personal-data/hooks/use-personal-data';
import type { MainFieldProps } from '@modules/dashboard/pages/docs-verification/docs-verification-page/tabs/personal-data/main-field/types';
import { getManualFieldName } from '@utils/verification/util';
import dayjs from 'dayjs';

export const MainField: FC<MainFieldProps> = ({ field }) => {
    const { watch } = useFormContext();
    const formValues = watch();
    const { data: personalDataFormValues } = usePersonalData();
    const { t } = useTranslation();
    let { label, placeholder } = getManualFieldName(field?.name);
    const genderOptions = useGenderOptions();
    const countryOptions = useCountryOptions();
    const { renderFlagOption, renderInputFlag } = useCountryAutocomplete();

    label = t(label);
    placeholder = t(placeholder);
    const maxLength =
        field.name === ManualFieldNamesEnum.PHONE
            ? FORM_MAX_LENGTH.PHONE_NUMBER
            : FORM_MAX_LENGTH.DEFAULT;

    switch (field.name) {
        case ManualFieldNamesEnum.FULL_NAME:
        case ManualFieldNamesEnum.EMAIL:
        case ManualFieldNamesEnum.PHONE:
        case ManualFieldNamesEnum.ADDRESS: {
            return (
                <FormTextField
                    name={field.name}
                    labelProps={{
                        label,
                    }}
                    textFieldProps={{
                        inputProps: { maxLength },
                        fullWidth: true,
                        hint: field.meta?.caption,
                        helperTextStatic: true,
                        placeholder,
                    }}
                />
            );
        }
        case ManualFieldNamesEnum.DOB: {
            return (
                <FormDatePicker
                    name={field.name}
                    labelProps={{
                        label,
                    }}
                    datePickerProps={{
                        disabledDate: (date?: Date) => dayjs(date).isAfter(new Date()),
                        hint: field.meta?.caption,
                        helperTextStatic: true,
                    }}
                />
            );
        }
        case ManualFieldNamesEnum.COUNTRY:
        case ManualFieldNamesEnum.CITIZENSHIP: {
            return (
                <FormAutocomplete
                    name={field.name}
                    options={countryOptions}
                    autocompleteProps={{
                        renderOption: renderFlagOption,
                        isOptionEqualToValue: (option, value) => option?.value === value?.value,
                        defaultValue: personalDataFormValues?.[field.name],
                    }}
                    textFieldProps={{
                        InputProps: {
                            startAdornment: renderInputFlag(formValues[field.name]?.value),
                        },
                        hint: field.meta?.caption,
                        helperTextStatic: true,
                    }}
                    labelProps={{
                        label,
                    }}
                />
            );
        }
        case ManualFieldNamesEnum.GENDER: {
            return (
                <FormAutocomplete
                    name={field.name}
                    options={genderOptions}
                    autocompleteProps={{
                        isOptionEqualToValue: (option, value) => option?.value === value?.value,
                        disableClearable: true,
                        defaultValue: personalDataFormValues?.[field.name],
                    }}
                    textFieldProps={{
                        fullWidth: true,
                        hint: field.meta?.caption,
                        helperTextStatic: true,
                    }}
                    labelProps={{
                        label,
                    }}
                />
            );
        }
    }

    return <React.Fragment />;
};
